import React, { useState } from 'react';
import { api } from '../../api/api';

interface AddAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssetAdded: () => void;
}

const AddAssetModal: React.FC<AddAssetModalProps> = ({ isOpen, onClose, onAssetAdded }) => {
  const [newAsset, setNewAsset] = useState({
    name: '',
    description: '',
    price: 0,
    // imagePath: '', // No longer directly set by user
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewAsset(prevAsset => ({
      ...prevAsset,
      [name]: name === 'price' ? parseFloat(value) : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      alert('Please select an image file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const uploadResponse = await api.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const imagePath = uploadResponse.data.filename; // Assuming backend returns filename

      // Now create the asset with the imagePath
      await api.post('/api/all/assets', { ...newAsset, imagePath });
      alert('Asset added successfully!');
      onAssetAdded(); // Callback to refresh asset list and close modal
      setNewAsset({ name: '', description: '', price: 0 }); // Reset form
      setSelectedFile(null); // Reset file input
    } catch (error) {
      console.error('Failed to add asset', error);
      alert('Failed to add asset.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-gray-800 p-8 rounded-lg shadow-lg w-full max-w-md border-2 border-gray-700">
        <h2 className="text-3xl font-bold text-white mb-6 text-center">Add New Asset</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-white text-lg font-bold mb-2">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={newAsset.name}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="description" className="block text-white text-lg font-bold mb-2">Description:</label>
            <textarea
              id="description"
              name="description"
              value={newAsset.description}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600 h-24"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="price" className="block text-white text-lg font-bold mb-2">Price:</label>
            <input
              type="number"
              id="price"
              name="price"
              value={newAsset.price}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600"
              required
            />
          </div>
          <div className="mb-6">
            <label htmlFor="imageFile" className="block text-white text-lg font-bold mb-2">Image File:</label>
            <input
              type="file"
              id="imageFile"
              name="imageFile"
              accept="image/*"
              onChange={handleFileChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600"
              required
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
            >
              Add Asset
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAssetModal;
